import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { ChevronDown, ChevronTop, Pencil } from 'assets/icons';
import CopyToClipboardLarge from 'components/copyToClipboard/copyToClipboardLarge';
import useModal from 'components/modal/useModal';
import { COMPANY_SUPPLIER_NONE_LABEL } from 'constants/missionSupplierFilters';
import { normalizePennylaneCategoryId } from 'helper/companyOverviewDiff';
import UseContext from 'hooks/useContext';
import usefetchdata from 'hooks/usefetchdata';
import ApiDatabase from 'server';

// Editing these fields touches billing and contract data, so it stays behind
// the same two roles the inline form was gated on.
const EDITOR_ROLES = [500, 515];

const iconButtonClass =
  'border border-gray-300 rounded-md w-8 h-8 flex justify-center items-center cursor-pointer hover:bg-gray-50 transition-colors flex-shrink-0';

const Card = ({ title, onEdit, collapsible = false, children }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  return (
    <div className={'border border-gray-100 rounded-lg p-1'}>
      <div className={'flex items-center justify-between px-4 py-2 gap-2'}>
        <div className={'truncate'}>{title}</div>
        <div className={'flex items-center gap-1 flex-shrink-0'}>
          {onEdit && (
            <button
              type={'button'}
              aria-label={`Modifier ${title}`}
              className={iconButtonClass}
              onClick={onEdit}
            >
              <Pencil wh={18} color={'#374151'} />
            </button>
          )}
          {collapsible && (
            <button
              type={'button'}
              aria-label={`${isCollapsed ? 'Déplier' : 'Replier'} ${title}`}
              className={iconButtonClass}
              onClick={() => setIsCollapsed((previous) => !previous)}
            >
              {isCollapsed ? (
                <ChevronDown wh={18} color={'#374151'} />
              ) : (
                <ChevronTop wh={18} color={'#374151'} />
              )}
            </button>
          )}
        </div>
      </div>
      {!isCollapsed && children}
    </div>
  );
};

const Field = ({ label, value }) => (
  <div className={'p-4 min-w-0'}>
    <CopyToClipboardLarge libelle={label} text={value} />
  </div>
);

const SlideOverCompanyOverview = ({ companyInfo, setCompanyInfo }) => {
  const { userData } = usefetchdata();
  const {
    objID,
    setModalData,
    modalDataReturn,
    setModalDataReturn,
    refreshCompanyBigAccount,
  } = UseContext();
  const { toggle } = useModal();
  const [isCostEstimateDisplay, setIsCostEstimateDisplay] = useState(false);
  const [bigAccount, setBigAccount] = useState('');
  const [collectiveAgreementDatas, setCollectiveAgreementDatas] = useState([]);
  const [pennylaneCategoryList, setPennylaneCategoryList] = useState([]);
  const [
    collectiveInstallationsReferential,
    setCollectiveInstallationsReferential,
  ] = useState([]);
  const [
    activeCollectiveInstallationCodes,
    setActiveCollectiveInstallationCodes,
  ] = useState([]);
  const token = localStorage.getItem('xsrfToken');
  const [role, setRole] = useState(500);

  useEffect(() => {
    if (userData?.role) setRole(userData.role);
  }, [userData]);

  const canEdit = EDITOR_ROLES.includes(role);

  useEffect(() => {
    ApiDatabase.getDisplayCostEstimate(
      { token, idCompany: objID },
      (data) => setIsCostEstimateDisplay(data?.displayCostEstimate),
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      },
    );
  }, []);

  const manageDisplayCostEstimate = () => {
    ApiDatabase.putDisplayCostEstimate(
      { token, idCompany: objID, displayCostEstimate: !isCostEstimateDisplay },
      () => {
        setIsCostEstimateDisplay(!isCostEstimateDisplay);
        toast.success(
          'Les estimations de coût sont maintenant visible par le client',
        );
      },
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      },
    );
  };

  const loadPennyLaneCategories = () =>
    ApiDatabase.getPennyLaneCategories(
      { token },
      (data) => setPennylaneCategoryList(data?.items || []),
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      },
    );

  useEffect(() => {
    loadPennyLaneCategories();
  }, []);

  useEffect(() => {
    ApiDatabase.getCompanyBigAccount(
      { token, idCompany: objID },
      (data) => setBigAccount(data?.bigAccount ?? ''),
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      },
    );
  }, [refreshCompanyBigAccount]);

  useEffect(() => {
    // RG-04: the installations list comes from the back, never hardcoded in the front.
    ApiDatabase.getCollectiveInstallations(
      { token },
      (data) => setCollectiveInstallationsReferential(data.installations ?? []),
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      },
    );
  }, []);

  useEffect(() => {
    if (!objID) return;
    ApiDatabase.getCollectiveInstallationsCompany(
      { token, idCompany: objID },
      (data) =>
        setActiveCollectiveInstallationCodes(
          (data.installations ?? []).map((installation) => installation.code),
        ),
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      },
    );
  }, [objID]);

  useEffect(() => {
    // Read-only here: the card shows the agreement's name, the modal owns the
    // editing. conv_collective itself is stored as an IDCC id.
    if (!token || !companyInfo) return;
    ApiDatabase.getCollectiveAgreement(
      { token },
      (data) =>
        setCollectiveAgreementDatas(
          (Array.isArray(data) ? data : []).map((item) => ({
            id: item._id,
            name: `${item.IDCC_id} - ${item.IDCC_name}`,
            checked:
              parseInt(companyInfo?.conv_collective) === parseInt(item.IDCC_id),
          })),
        ),
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      },
    );
  }, [token, companyInfo?.conv_collective]);

  useEffect(() => {
    if (modalDataReturn?.description) {
      setCompanyInfo((previous) => ({
        ...previous,
        description: modalDataReturn.description,
      }));
      setModalDataReturn({});
    }
  }, [modalDataReturn]);

  useEffect(() => {
    if (modalDataReturn?.type === 'collective_installations_updated') {
      setActiveCollectiveInstallationCodes(
        (modalDataReturn.installations ?? []).map(
          (installation) => installation.code,
        ),
      );
      setModalDataReturn({});
    }
  }, [modalDataReturn]);

  useEffect(() => {
    if (
      modalDataReturn?.type === 'company_legal_information_updated' ||
      modalDataReturn?.type === 'company_activity_updated'
    ) {
      setCompanyInfo((previous) => ({
        ...previous,
        ...modalDataReturn.companyInfo,
      }));
      setModalDataReturn({});
    }
  }, [modalDataReturn]);

  useEffect(() => {
    // Creating a category replaces the activity modal, so it is reopened here
    // once the new category exists — otherwise the edit in progress is lost.
    if (modalDataReturn?.createdCategory) {
      loadPennyLaneCategories();
      setModalDataReturn({});
      setModalData({ idCompany: companyInfo?._id, companyInfo });
      toggle('company_activity');
    }
  }, [modalDataReturn]);

  const openModal = (modalType) => {
    setModalData({ idCompany: companyInfo?._id, companyInfo });
    toggle(modalType);
  };

  const selectedPennylaneCategory = pennylaneCategoryList.find(
    (category) =>
      normalizePennylaneCategoryId(category.id) ===
      normalizePennylaneCategoryId(companyInfo?.pennylane_category_id),
  );

  const activeCollectiveInstallationLabels = collectiveInstallationsReferential
    .filter((installation) =>
      activeCollectiveInstallationCodes.includes(installation.code),
    )
    .map((installation) => installation.label);

  const location = companyInfo?.address
    ? [
        companyInfo.address.city,
        companyInfo.address.department,
        companyInfo.address.street,
      ]
        .filter(Boolean)
        .join(', ')
    : 'Non renseigné';

  const collectiveAgreementLabel =
    collectiveAgreementDatas?.find((agreement) => agreement.checked)?.name ||
    companyInfo?.conv_collective ||
    'Non renseigné';

  return (
    <>
      <Card
        title={'Informations légales'}
        collapsible
        onEdit={canEdit ? () => openModal('company_legal_information') : null}
      >
        <div
          className={
            'border border-gray-100 rounded-lg overflow-hidden grid grid-cols-2 divide-x divide-y divide-gray-100'
          }
        >
          <Field
            label={'Raison sociale'}
            value={companyInfo?.reason_social || 'Non renseigné'}
          />
          <Field
            label={'Nom commercial'}
            value={companyInfo?.name_commercial || 'Non renseigné'}
          />
          <Field
            label={'SIRET'}
            value={companyInfo?.siret || 'Non renseigné'}
          />
          <Field
            label={'Forme juridique'}
            value={companyInfo?.status || 'Non renseigné'}
          />
          <Field
            label={'Fournisseur'}
            value={
              companyInfo?.companySupplier?.name || COMPANY_SUPPLIER_NONE_LABEL
            }
          />
          <Field
            label={'Code APE'}
            value={companyInfo?.code_ape || 'Non renseigné'}
          />
          <Field
            label={'Effectif'}
            value={companyInfo?.staff || 'Non renseigné'}
          />
          <Field label={'Localisation'} value={location} />
          <Field
            label={'Convention collective'}
            value={collectiveAgreementLabel}
          />
        </div>
      </Card>

      <Card
        title={"Activité de l'entreprise"}
        collapsible
        onEdit={canEdit ? () => openModal('company_activity') : null}
      >
        <div
          className={
            'border border-gray-100 rounded-lg overflow-hidden grid grid-cols-2 divide-x divide-y divide-gray-100'
          }
        >
          <Field
            label={'Secteur Pennylane'}
            value={selectedPennylaneCategory?.label || 'Non renseigné'}
          />
          <Field
            label={'Grand compte'}
            value={bigAccount?.name || 'Non renseigné'}
          />
        </div>
      </Card>

      <Card
        title={"Brief de l'entreprise"}
        onEdit={() => openModal('edit_description_company')}
      >
        <div
          className={
            'border border-gray-100 rounded-lg px-4 py-3 text-gray-500'
          }
        >
          {companyInfo?.description || 'Aucun brief renseigné'}
        </div>
      </Card>

      <Card
        title={'Installation collective'}
        onEdit={() => openModal('collective_installations')}
      >
        <div
          className={
            'border border-gray-100 rounded-lg px-4 py-3 text-gray-500'
          }
        >
          {activeCollectiveInstallationLabels.length > 0
            ? activeCollectiveInstallationLabels.join(', ')
            : 'Aucune installation collective'}
        </div>
      </Card>

      {/* Not part of the mockup, kept because removing it would drop a feature. */}
      <div className={'border border-gray-200 rounded-lg p-3'}>
        <div className={'flex justify-between items-center'}>
          <div className={''}>Estimations de coût</div>
          <input
            type={'checkbox'}
            checked={isCostEstimateDisplay}
            onChange={() => manageDisplayCostEstimate()}
          />
        </div>
      </div>
    </>
  );
};

export default SlideOverCompanyOverview;
